// Onboarding diagnostics (web) — same events and rules as the mobile app
// (apps/mobile/src/diagnostics/onboardingDiagnostics.js):
//   * never throws into or blocks the UI;
//   * events are queued in localStorage and sent in batches to the existing
//     server log (POST /analytics/activation), each with a client id so a
//     re-send is stored once; failures are kept for the next try;
//   * events belong to the signed-in user who produced them;
//   * only non-sensitive fields (step, CTA kind, error class / HTTP status /
//     machine code, browser OS family, screen size).
// Mirrored to GA4 through the existing analytics boundary.
import {
    ONBOARDING_VERSION,
    ONBOARDING_STEPS,
    ONBOARDING_DIAGNOSTIC_EVENTS as EVENTS,
    describeOnboardingError,
} from '@aluvo/constants/src/onboardingDiagnostics';
import { apiRequest } from '../services/api';
import { ANALYTICS_EVENTS, trackEvent } from '../analytics';

export const OUTBOX_KEY = 'aluvo_diag_outbox';
const MAX_QUEUE = 200;
const MAX_BATCH = 50;
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const FLUSH_DELAY_MS = 2000;
const ERROR_FLUSH_DELAY_MS = 300;
const RETRY_DELAY_MS = 30000;

const STEP_NUMBERS = Object.fromEntries(Object.values(ONBOARDING_STEPS).map((s) => [s.key, s.number]));
const randomId = (prefix) => `${prefix}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}${Math.random().toString(36).slice(2, 6)}`;

function sessionId() {
    try {
        let id = sessionStorage.getItem('aluvo_diag_session');
        if (!id) { id = randomId('ses'); sessionStorage.setItem('aluvo_diag_session', id); }
        return id;
    } catch {
        return 'ses_unavailable';
    }
}

let queue = null;
let userId = null;
let seq = 0;
let flushTimer = null;
let flushing = false;
let activeStep = null;

/**
 * The signed-in user's id from the persisted session. On a page reload the
 * onboarding page renders (and records) before AuthContext's restore effect
 * runs, so this is the fallback until setDiagnosticsUser is called.
 */
export function storedUserId() {
    try {
        const stored = JSON.parse(localStorage.getItem('aluvo_user') || 'null');
        const id = stored?.id || stored?._id;
        return id ? String(id) : null;
    } catch {
        return null;
    }
}

function readQueue() {
    if (queue) return queue;
    try {
        const stored = JSON.parse(localStorage.getItem(OUTBOX_KEY) || '[]');
        queue = Array.isArray(stored) ? stored : [];
    } catch {
        queue = [];
    }
    return queue;
}

function writeQueue() {
    try { localStorage.setItem(OUTBOX_KEY, JSON.stringify(queue || [])); } catch { /* storage full / blocked */ }
}

/** Browser OS family from the User-Agent; the raw User-Agent is never sent. */
export function osFamilyFromUserAgent(ua = '') {
    if (/android/i.test(ua)) return 'android';
    if (/iphone|ipad|ipod/i.test(ua) || (/macintosh/i.test(ua) && /mobile/i.test(ua))) return 'ios';
    if (/cros/i.test(ua)) return 'chromeos';
    if (/windows/i.test(ua)) return 'windows';
    if (/mac os x|macintosh/i.test(ua)) return 'macos';
    if (/linux/i.test(ua)) return 'linux';
    return null;
}

export function webDeviceContext(win = typeof window !== 'undefined' ? window : undefined) {
    const width = Math.round(win?.innerWidth || 0) || null;
    const height = Math.round(win?.innerHeight || 0) || null;
    const os = osFamilyFromUserAgent(win?.navigator?.userAgent || '');
    return {
        app_version: process.env.REACT_APP_VERSION || null,
        build_number: process.env.REACT_APP_BUILD_NUMBER || null,
        // Web has no OS version we can trust (user-agent reduction); the OS family is recorded instead.
        os_version: os,
        device_class: !width ? null : width < 768 ? 'phone' : width < 1024 ? 'tablet' : 'desktop',
        screen_width: width,
        screen_height: height,
        onboarding_version: ONBOARDING_VERSION,
    };
}

function scheduleFlush(delay = FLUSH_DELAY_MS) {
    if (flushTimer) {
        if (delay >= FLUSH_DELAY_MS) return;
        clearTimeout(flushTimer);
    }
    flushTimer = setTimeout(() => { flushTimer = null; flushDiagnostics(); }, delay);
}

/** Send queued events for the signed-in user. Resolves, never rejects. */
export async function flushDiagnostics() {
    if (flushing || !userId) return;
    flushing = true;
    try {
        readQueue();
        const now = Date.now();
        queue = queue.filter((e) => now - e.at < MAX_AGE_MS);
        const batch = queue.filter((e) => e.userId === userId).slice(0, MAX_BATCH);
        if (!batch.length) return;
        let drop = true;
        try {
            await apiRequest('/analytics/activation', {
                method: 'POST',
                body: {
                    events: batch.map((e) => ({
                        eventType: e.eventType,
                        properties: e.properties,
                        clientEventId: e.clientEventId,
                        sessionId: e.sessionId,
                        ageMs: Math.max(0, now - e.at),
                        ...(e.context && { context: e.context }),
                    })),
                },
                retry: { retries: 0, timeoutMs: 10000 },
                quiet: true,
            });
        } catch (error) {
            const status = error?.statusCode;
            drop = Number.isInteger(status) && status >= 400 && status < 500 && ![401, 408, 429].includes(status);
            if (!drop) scheduleFlush(RETRY_DELAY_MS);
        }
        if (drop) {
            const sent = new Set(batch.map((e) => e.clientEventId));
            queue = queue.filter((e) => !sent.has(e.clientEventId));
            writeQueue();
            if (queue.some((e) => e.userId === userId)) scheduleFlush(ERROR_FLUSH_DELAY_MS);
        }
    } catch { /* never */ } finally {
        flushing = false;
    }
}

function record(eventType, step, extra = {}, { withContext = false, urgent = false } = {}) {
    try {
        const properties = {
            ...(step && { step, step_number: STEP_NUMBERS[step] }),
            platform: 'web',
            app_version: process.env.REACT_APP_VERSION || undefined,
            onboarding_version: ONBOARDING_VERSION,
            ...extra,
        };
        if (Object.values(ANALYTICS_EVENTS).includes(eventType)) {
            try { trackEvent(eventType, { ...properties, onboarding_session_id: sessionId() }); } catch { /* analytics never breaks onboarding */ }
        }
        if (!userId) userId = storedUserId();
        if (!userId) return;
        seq += 1;
        readQueue().push({
            userId,
            eventType,
            properties: { ...properties, seq },
            clientEventId: randomId('evt'),
            sessionId: sessionId(),
            at: Date.now(),
            ...(withContext && { context: webDeviceContext() }),
        });
        if (queue.length > MAX_QUEUE) queue = queue.slice(queue.length - MAX_QUEUE);
        writeQueue();
        scheduleFlush(urgent ? ERROR_FLUSH_DELAY_MS : FLUSH_DELAY_MS);
    } catch { /* never */ }
}

export const OnboardingDiagnostics = {
    stepViewed(step, extra = {}) {
        activeStep = step;
        record(EVENTS.STEP_VIEWED, step, extra, { withContext: true });
    },
    stepAction(step, action, extra = {}) {
        record(EVENTS.STEP_ACTION, step, { action, ...extra });
    },
    stepError(step, { operation, endpoint = null, error = null, blocking = true, durationMs = null, extra = {} } = {}) {
        record(EVENTS.STEP_ERROR, step, {
            operation,
            ...(endpoint && { endpoint }),
            ...describeOnboardingError(error),
            blocking,
            ...(Number.isFinite(durationMs) && { duration_ms: Math.round(durationMs) }),
            ...extra,
        }, { urgent: true });
    },
    /** The app bypassed a step that had nothing to show (`reason`: ONBOARDING_SKIP_REASONS). */
    stepAutoSkipped(step, reason, extra = {}) {
        record(EVENTS.STEP_AUTO_SKIPPED, step, { reason, ...extra });
    },
    /** A title picked / un-picked. GA4 only: the count that matters goes with Continue / Skip. */
    titleToggled(selected, { selectedCount, mediaType = null } = {}) {
        try {
            trackEvent(selected ? ANALYTICS_EVENTS.TITLE_SELECTED : ANALYTICS_EVENTS.TITLE_DESELECTED, {
                step: 'titles',
                step_number: STEP_NUMBERS.titles,
                platform: 'web',
                app_version: process.env.REACT_APP_VERSION || undefined,
                onboarding_version: ONBOARDING_VERSION,
                onboarding_session_id: sessionId(),
                selected_count: selectedCount,
                ...(mediaType && { media_type: mediaType }),
            });
        } catch { /* analytics never breaks onboarding */ }
    },
    resumed(step, { restoredStep } = {}) {
        record(EVENTS.RESUMED, step, { ...(restoredStep && { restored_step: restoredStep }) });
    },
    quizSubmitFailed({ error, attemptId = null, sourceScreen = null }) {
        record(EVENTS.QUIZ_SUBMIT_FAILED, null, {
            ...describeOnboardingError(error),
            ...(attemptId && { attempt_id: String(attemptId) }),
            ...(sourceScreen && { source_screen: sourceScreen }),
            reported_by: 'client',
        }, { urgent: true });
    },
    finished() {
        activeStep = null;
        flushDiagnostics();
    },
};

export function getActiveOnboardingStep() {
    return activeStep;
}

/** Bind to the signed-in user (null on logout); other users' queued events are discarded. */
export function setDiagnosticsUser(id) {
    try {
        const next = id ? String(id) : null;
        if (next === userId) return;
        userId = next;
        readQueue();
        const before = queue.length;
        queue = queue.filter((e) => e.userId === next);
        if (queue.length !== before) writeQueue();
        if (next) scheduleFlush(ERROR_FLUSH_DELAY_MS);
    } catch { /* never */ }
}

// A tab hidden mid-onboarding (switched away / closed): try to deliver now.
if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') flushDiagnostics();
    });
}

export function resetDiagnosticsForTests() {
    queue = null;
    userId = null;
    seq = 0;
    clearTimeout(flushTimer);
    flushTimer = null;
    flushing = false;
    activeStep = null;
}

export function getQueuedEventsForTests() {
    return readQueue().slice();
}
